import React from 'react'

import { TextClamp } from '../components/text/TextClamp'
import { clampArray } from '../foundations/slide-limits'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { SlideFrame } from '../layouts/SlideFrame'
import { SlideHeaderChrome } from '../layouts/SlideHeaderChrome'
import { registerSlide } from './registry'
import { presetSlot } from '../layouts/presetSlots'
import { SlotActionCards, SlotHeadline } from '../slots'
import { EditableText } from './EditableText'
import type { EditableSlideProps } from './contracts'
import type { RecommendationsSlideData } from './renderer-internal-types'

// The stage binding (plots plan Step 7, AF): recommendations draws its own full-bleed
// main at the bound body rect, with the bound headline and intro boxes.
const BODY = presetSlot('recommendations', 'body').rect!
const HEADLINE = presetSlot('recommendations', 'headline').text!
const INTRO = presetSlot('recommendations', 'narrative').text!
const INTRO_BULLETS = presetSlot('recommendations', 'bullets').text!

function RecommendationsSlideComponent(props: RecommendationsSlideData & EditableSlideProps) {
  const { editable, onEdit } = props
  const resolvedActionTitle = sanitizeNarrativeText(props.actionTitle)
  const logoUrl = props.clientLogoUrl ?? null
  const actions = clampArray(props.actions || [], 'recommendations', 'actions')
  const bullets = (props.introBullets || []).slice(0, 4)

  return (
    <SlideFrame fullBleed editable={editable} onEdit={onEdit}>
      <div data-testid="recommendations-layout" style={{ position: 'absolute', inset: 0, background: 'var(--ds-slide-bg)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
          <SlideHeaderChrome
            eyebrowText={props.eyebrow || ''}
            clientLogoUrl={logoUrl}
            eyebrowTextTransform="none"
          />
        </div>

        {/*
          Height budget (main block is 1320x674):
          - h1 actionTitle: 1 line @ 40px/1.2 = 48px
          - marginTop to intro grid: 22px
          - intro grid row: max(introParagraph col, bullets col)
            - introParagraph: up to 7 lines @ 20px/1.5 = 210px (fits schema-max 400 chars in the
              ~654px column at 20px; 5 lines clipped a max-length paragraph, see recommendations.schema.json)
            - bullets: schema-max 4 items (recommendations.schema.json maxItems 4) @ 18px/1.5 x2
              lines + 8px gaps = 4x2x27 + 3x8 = 240px - taller than introParagraph, so the
              4-bullet case, not the 3-bullet case, sets the worst-case row height
          - gap before cards: 32px
          - cards row: flex:1, absorbs remaining space:
            674 - (48 + 22 + 240) - 32 = 332px at the 4-bullet worst case (362px at 3 bullets,
            since the row then drops to introParagraph's 210px), floor of 283px (minHeight) so it
            never shrinks below today's size
          Card interior overhead = 48 padding + 36 header row + 8 gap = 92px, so the body's
          guaranteed space is 332 - 92 = 240px (4 bullets) or 362 - 92 = 270px (3 bullets). At
          16px/1.6 (25.6px/line) that is ~9.3 and ~10.5 lines. A schema-max 420-char body only
          ever wraps to ~9 lines, so maxLines=10 gives it one full line of headroom while sitting
          at (not above) the guaranteed floor - unlike the previous maxLines=11, which exceeded
          both worst cases and clipped the last line with no ellipsis.
        */}
        <main style={{ position: 'absolute', left: BODY.x, top: BODY.y, width: BODY.w, height: BODY.h, display: 'flex', flexDirection: 'column', gap: 32 }}>
          <div>
            <SlotHeadline text={resolvedActionTitle || ''} maxLines={HEADLINE.maxLines} width={HEADLINE.width} widthWhenEditing editable={editable} onEdit={onEdit} />
            <div data-testid="recommendations-intro" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 22 }}>
              {editable && onEdit ? (
                <EditableText
                  value={props.introParagraph || props.actionSubtitle || ''}
                  field="introParagraph"
                  editable
                  onEdit={onEdit}
                  tag="p"
                  multiline
                  style={{ fontSize: 20, color: 'var(--slide-text-primary)', lineHeight: '1.5' }}
                />
              ) : (
                <TextClamp minLines={2} maxLines={INTRO.maxLines} lineHeight="1.5" style={{ fontSize: 20, color: 'var(--slide-text-primary)' }}>
                  {props.introParagraph || props.actionSubtitle || ''}
                </TextClamp>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {bullets.map((bullet, i) => (
                  <div key={bullet} style={{ display: 'grid', gridTemplateColumns: '8px 1fr', columnGap: 10, alignItems: 'baseline' }}>
                    <span style={{ color: 'var(--ds-exec-kpi)', fontSize: 18, lineHeight: '1.5' }}>•</span>
                    {editable && onEdit ? (
                      <EditableText
                        value={bullet}
                        field={`introBullets.${i}`}
                        editable
                        onEdit={onEdit}
                        tag="span"
                        style={{ color: 'var(--slide-text-primary)', fontSize: 18, lineHeight: '1.5' }}
                      />
                    ) : (
                      <TextClamp minLines={1} maxLines={INTRO_BULLETS.maxLines} lineHeight="1.5" style={{ color: 'var(--slide-text-primary)', fontSize: 18 }}>
                        {bullet}
                      </TextClamp>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <SlotActionCards actions={actions} editable={editable} onEdit={onEdit} renderMode={props.renderMode} />
        </main>
      </div>
    </SlideFrame>
  )
}

export const RecommendationsSlide = registerSlide('recommendations', RecommendationsSlideComponent)
